import { getCookie } from './utils';
import { createUUIDv4 } from '../../util/helpers';

const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';
const CSRF_COOKIE_NAME = 'csrf_token';

/**
 * Refresh CSRF token by calling the token endpoint
 */
async function refreshCSRFToken() {
  try {
    // Use native fetch without interceptors to avoid recursion
    const response = await window.fetch('/api/v1/csrf-token/', {
      method: 'GET',
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`CSRF refresh failed: ${response.status}`);
    }

    console.log('CSRF token refreshed from /api/v1/csrf-token/');
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
    throw error;
  }
}

/**
 * Ensure we have a valid CSRF token in the cookie
 */
async function ensureFreshToken() {
  let token = getCookie(CSRF_COOKIE_NAME);
  if (!token) {
    console.log('No CSRF token cookie found, attempting to refresh.');
    await refreshCSRFToken();
    token = getCookie(CSRF_COOKIE_NAME);
    if (!token) {
      console.error('CSRF token still not available after refresh.');
      throw new Error('CSRF token unavailable after refresh attempt');
    }
  }
  return token;
}

function buildQueryString(params, { traditional = false, paramsStyle } = {}) {
  const style = paramsStyle || (traditional ? 'traditional' : 'json');
  const qs = new URLSearchParams();

  const appendPair = (key, val) => {
    // Normalize values (Dates, numbers, booleans)
    const v = val instanceof Date ? val.toISOString() : val;
    if (v !== null && v !== undefined) qs.append(key, String(v));
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      if (style === 'traditional') {
        // role=1&role=2&role=3
        value.forEach(v => appendPair(key, v));
      } else if (style === 'brackets') {
        // role[]=1&role[]=2&role[]=3
        value.forEach(v => appendPair(`${key}[]`, v));
      } else {
        // json (current default): role=[1,2,3]
        appendPair(key, JSON.stringify(value));
      }
    } else if (typeof value === 'object' && !(value instanceof Date) && !(value instanceof Blob) && !(value instanceof File)) {
      // Simple object support: JSON by default; for other styles, flatten as key.sub=...
      if (style === 'json') {
        appendPair(key, JSON.stringify(value));
      } else {
        Object.entries(value).forEach(([subKey, subVal]) => {
          appendPair(`${key}.${subKey}`, subVal);
        });
      }
    } else {
      appendPair(key, value);
    }
  });

  return qs.toString();
}


/**
 * Enhanced fetch wrapper with CSRF protection and auto-retry
 * Supports both JSON and FormData
 *
 * @param {string} url - Request URL
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
async function fetchWithCSRF(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const modifyingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];

  // Clone options to avoid mutating original
  const fetchOptions = { ...options };
  fetchOptions.headers = new Headers(fetchOptions.headers || {});

  // Always include credentials for cookie handling
  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'same-origin';
  }

  // Handle query parameters (axios-style 'params')
  if (fetchOptions.params) {
    const queryString = buildQueryString(fetchOptions.params, {
      traditional: fetchOptions.traditional === true,
      paramsStyle: fetchOptions.paramsStyle, // 'traditional' | 'brackets' | 'json'
    });

    if (queryString) {
      url = url + (url.includes('?') ? '&' : '?') + queryString;
    }

    // Clean up non-fetch options
    delete fetchOptions.params;
    delete fetchOptions.traditional;
    delete fetchOptions.paramsStyle;
  }

  // Add CSRF token and correlation ID for modifying requests
  if (modifyingMethods.includes(method)) {
    const token = await ensureFreshToken();

    if (token) {
      fetchOptions.headers.set(CSRF_HEADER_NAME, token);
    } else {
      const error = new Error('CSRF token is not available for a modifying request');
      error.response = {
        status: 403,
        statusText: 'Forbidden',
        data: { error: 'CSRF token unavailable' },
      };
      throw error;
    }

    // Add correlation ID for request tracking
    fetchOptions.headers.set('X-Correlation-ID', createUUIDv4());
  }

  try {
    const response = await fetch(url, fetchOptions);

    // Check if it's a CSRF error (403/400 with CSRF in error message)
    if ((response.status === 403 || response.status === 400) && !fetchOptions._retry) {
      // Try to read the error message
      const contentType = response.headers.get('content-type');
      let isCSRFError = false;

      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.clone().json();
        if (
          errorData?.error &&
          typeof errorData.error === 'string' &&
          errorData.error.includes('CSRF')
        ) {
          isCSRFError = true;
        }
      }

      if (isCSRFError) {
        console.log('CSRF error detected, attempting to refresh token and retry.');

        // Refresh token
        await refreshCSRFToken();
        const newToken = getCookie(CSRF_COOKIE_NAME);

        if (newToken) {
          // Update header with new token and mark as retry
          fetchOptions.headers.set(CSRF_HEADER_NAME, newToken);
          fetchOptions._retry = true;

          // Retry the request
          return fetch(url, fetchOptions);
        } else {
          console.error('CSRF token still not available after refresh on error.');
        }
      }
    }

    return response;
  } catch (error) {
    console.error('Fetch request failed:', error);
    throw error;
  }
}

/**
 * Helper to handle common response patterns
 * Creates axios-compatible error structure for consistency
 *
 * @param {Response} response - Fetch Response object
 * @returns {Promise<any>} Parsed response data
 * @throws {Error} Error with axios-compatible structure
 */
async function handleResponse(response) {
  const contentType = response.headers.get('content-type');

  if (!response.ok) {
    let errorData = null;

    // Try to parse error response body
    if (contentType && contentType.includes('application/json')) {
      try {
        errorData = await response.json();
      } catch (e) {
        // If JSON parsing fails, use status text
        errorData = { message: response.statusText };
      }
    } else {
      // Non-JSON error response
      try {
        const text = await response.text();
        errorData = { message: text || response.statusText };
      } catch (e) {
        errorData = { message: response.statusText };
      }
    }

    // Create axios-compatible error structure
    const error = new Error(
      errorData?.msg ||
      errorData?.message ||
      errorData?.error ||
      `HTTP ${response.status}: ${response.statusText}`
    );

    // Add axios-compatible response object
    error.response = {
      status: response.status,
      statusText: response.statusText,
      data: errorData,
      headers: response.headers,
    };

    // Keep for backward compatibility with existing code
    error.data = errorData;
    error.status = response.status;

    throw error;
  }

  // Success response
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

/**
 * Prepare request body and headers based on data type
 * Handles JSON, FormData, and other types
 */
function prepareRequestBody(data, headers) {
  // If data is FormData, use it directly and DON'T set Content-Type
  // (browser will set it with boundary automatically)
  if (data instanceof FormData) {
    return { body: data, headers };
  }

  // If data is a File or Blob, wrap in FormData
  if (data instanceof File || data instanceof Blob) {
    const formData = new FormData();
    formData.append('file', data);
    return { body: formData, headers };
  }

  // Default: JSON
  if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  return { body: JSON.stringify(data), headers };
}

/**
 * Convenience methods that mirror axios API
 */
const api = {
  /**
   * GET request (returns raw Response)
   */
  get: (url, config = {}) => fetchWithCSRF(url, { ...config, method: 'GET' }),

  /**
   * GET request with automatic response handling
   * @throws {Error} Error with axios-compatible structure (error.response.status, error.response.data)
   */
  Get: async (url, config = {}) => {
    const response = await api.get(url, config);
    return handleResponse(response);
  },

  /**
   * POST request (returns raw Response)
   */
  post: (url, data, config = {}) => {
    const headers = { ...config.headers };
    const { body, headers: finalHeaders } = prepareRequestBody(data, headers);

    return fetchWithCSRF(url, {
      ...config,
      method: 'POST',
      headers: finalHeaders,
      body,
    });
  },

  /**
   * POST request with automatic response handling
   * @throws {Error} Error with axios-compatible structure
   */
  Post: async (url, data, config = {}) => {
    const response = await api.post(url, data, config);
    return handleResponse(response);
  },

  /**
   * PUT request (returns raw Response)
   */
  put: (url, data, config = {}) => {
    const headers = { ...config.headers };
    const { body, headers: finalHeaders } = prepareRequestBody(data, headers);

    return fetchWithCSRF(url, {
      ...config,
      method: 'PUT',
      headers: finalHeaders,
      body,
    });
  },

  /**
   * PUT request with automatic response handling
   * @throws {Error} Error with axios-compatible structure
   */
  Put: async (url, data, config = {}) => {
    const response = await api.put(url, data, config);
    return handleResponse(response);
  },

  /**
   * PATCH request (returns raw Response)
   */
  patch: (url, data, config = {}) => {
    const headers = { ...config.headers };
    const { body, headers: finalHeaders } = prepareRequestBody(data, headers);

    return fetchWithCSRF(url, {
      ...config,
      method: 'PATCH',
      headers: finalHeaders,
      body,
    });
  },

  /**
   * PATCH request with automatic response handling
   * @throws {Error} Error with axios-compatible structure
   */
  Patch: async (url, data, config = {}) => {
    const response = await api.patch(url, data, config);
    return handleResponse(response);
  },

  /**
   * DELETE request (returns raw Response)
   */
  delete: (url, config = {}) => fetchWithCSRF(url, { ...config, method: 'DELETE' }),

  /**
   * DELETE request with automatic response handling
   * @throws {Error} Error with axios-compatible structure
   */
  Delete: async (url, config = {}) => {
    const response = await api.delete(url, config);
    return handleResponse(response);
  },
};

export { fetchWithCSRF, api, handleResponse };
export default api;
